/**
 * ChatWindow - 통합 채팅 윈도우 컴포넌트
 *
 * buyer/vendor 공통 채팅 UI를 제공합니다.
 * Slots 패턴을 통해 앱별 모달/패널 커스터마이징을 지원합니다.
 *
 * @example
 * // Buyer (Semo-1)
 * <ChatWindow
 *   roomId={roomId}
 *   role="buyer"
 *   currentUserId={buyerId}
 *   supabaseClient={supabase}
 *   onClose={handleClose}
 *   slots={{
 *     quoteDetailModal: (props) => <QuoteSheetModal {...props} />,
 *   }}
 *   onCreateContractDirect={openContractModal}
 * />
 *
 * @example
 * // Vendor (vendor-dashboard)
 * <ChatWindow
 *   roomId={roomId}
 *   role="vendor"
 *   currentUserId={supplierId}
 *   supabaseClient={supabase}
 *   onClose={handleClose}
 *   slots={{
 *     quoteDetailModal: (props) => <QuoteSheetModal {...props} />,
 *     quoteActionModal: (props) => <BaseQuoteModal {...props} />,
 *   }}
 * />
 */

import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  useChatMessages,
  useUnreadMessages,
  usePresenceStatus,
  useAutoMarkRead,
  useChatTyping,
} from "../../hooks";
import { useFileDownload } from "@semo/shared-ui";
import {
  useChatControllerGetRoom,
  useContractsControllerGetCardPaymentStatus,
  chatControllerSendMessageBodyContentMax,
  requestContractDocuments,
  uploadContractDocumentFile,
  submitContractDocumentInput,
  submitContractDocumentBuyerConfirmation,
  chatControllerGetMessages,
  getChatControllerGetMessagesQueryKey,
} from "@semo/api-contracts";
import { useQueryClient } from "@tanstack/react-query";
import { formatDateKoreanLong, openInnopayReceipt } from "@semo/shared-utils";
import {
  ShieldCheck,
  AlertCircle,
  ArrowDown,
  MapPin,
  User,
  UserRound,
  Phone,
  Mail,
  FileText,
  PanelLeftClose,
  PanelLeftOpen,
} from "@semo/icons";
import { toast } from "sonner";
import { ChatHeader } from "./ChatHeader";
import { ChatInput } from "./ChatInput";
import { ChatMessage } from "../ChatMessage";
import { ChatDateDivider } from "../atoms/ChatDateDivider";
import { PartyAvatar } from "../atoms/PartyAvatar";
import { TypingIndicator } from "../atoms/TypingIndicator";
import { CancellationReceiptModal } from "../modals/CancellationReceiptModal";
import {
  ChatListSkeleton,
  ChatWindowSkeleton,
  Badge,
  Separator,
  MaskedSection,
  ButtonShadcn as Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@semo/shared-ui";
import { cn } from "@semo/shared-ui";
import {
  ChatActionContext,
  type ChatActionContextType,
} from "../../context/ChatActionContext";
import { groupMessages } from "../../utils/message-grouper";
import {
  FILE_ATTACH_FAILED_MESSAGE,
  clientErrorMessage,
} from "../../utils/chat-error-messages";
import type {
  SupplierDto,
  BuyerDto,
  ContractMessageResponseDto,
  PaymentPendingMessageResponseDto,
} from "@semo/api-contracts";
import { TextMessageResponseDtoSenderType } from "@semo/api-contracts";
import type { ChatWindowProps, ChatWindowState } from "./types";
import { CardPaymentSignupBanner } from "../cards/CardPaymentSignupBanner";
import { QuoteCard } from "../cards/QuoteCard";
import { QuoteMessageResponseDto } from "@semo/api-contracts";

/**
 * Extract display info for ChatHeader
 */
function getPartyDisplayInfo(
  role: "buyer" | "supplier",
  supplierInfo?: SupplierDto,
  buyerInfo?: BuyerDto,
): {
  id: string | null;
  name: string;
  imageUrl: string | null;
  trustPercentile?: number | null;
} {
  if (role === "buyer") {
    return {
      id: supplierInfo?.id || null,
      name: supplierInfo?.companyName || "업체명",
      imageUrl: supplierInfo?.profileImageUrl || null,
      trustPercentile: supplierInfo?.trustPercentile,
    };
  } else {
    return {
      id: buyerInfo?.id || null,
      name: buyerInfo?.name || "고객",
      imageUrl: buyerInfo?.profileImageUrl || null,
    };
  }
}

function ChatWindowComponent({
  roomId,
  chatRole: role,
  currentUserId,
  supabaseClient,
  onClose,
  onContractComplete,
  onContractAction,
  onContractReject,
  onCreateContractDirect,
  onOpenCmarketContracts,
  slots,
  fileInputRef: externalFileInputRef,
  readOnly = false,
  initialRoomData,
  disableRoomFetch = false,
  variant = "modal",
  enableMessageFetch = true,
  hideClose = false,
  isInitializing = false,
}: ChatWindowProps & { enableMessageFetch?: boolean }) {
  // Internal file input ref if not provided externally
  const internalFileInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = externalFileInputRef ?? internalFileInputRef;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);

  // State
  const [state, setState] = useState<ChatWindowState>({
    inputText: "",
    selectedQuote: null,
    selectedContractId: null,
    editingQuote: null,
    showCancellationModal: false,
    showCancellationReceiptModal: false,
    selectedCancellationMessage: null,
  });

  // Fetch room data (Conditional)
  const { data: joinData, isLoading: joinApiLoading } =
    useChatControllerGetRoom(roomId, {
      query: {
        enabled: !disableRoomFetch,
      },
    });

  // React Query cache 강제 invalidate 용
  // (useInfiniteQuery.refetch() 만으로는 realtime broadcast 를 놓친 경우 카드 항목이
  //  갱신되지 않는 케이스가 있어, contract-document 파일 첨부 후 명시적으로 invalidate 한다)
  const queryClient = useQueryClient();

  // Determine effective room data
  const effectiveJoinData = disableRoomFetch
    ? initialRoomData
    : (joinData?.data ?? initialRoomData);
  const roomData = effectiveJoinData;

  const activeRoomData = roomData;

  // Loading logic
  const joinLoading = activeRoomData
    ? false
    : disableRoomFetch
      ? false
      : joinApiLoading;

  // File download hook
  const { downloadFile } = useFileDownload();

  // Extract display info for ChatHeader
  const partyDisplay = getPartyDisplayInfo(
    role,
    activeRoomData?.supplierInfo,
    activeRoomData?.userInfo,
  );

  // Chat messages hook
  const {
    messages,
    isLoading: messagesLoading,
    isSending,
    isUploading,
    isSubmittingQuote,
    isCreatingContract,
    sendMessage,
    uploadFile: uploadFileHook,
    submitQuoteRevision,
    requestContractCancellation,
    markMessagesAsRead,
    hasMoreMessages,
    isFetchingPrevious,
    fetchPreviousMessages,
    refetch,
    resendMessage,
  } = useChatMessages({
    roomId,
    currentUserId,
    currentUserType:
      role === "buyer"
        ? TextMessageResponseDtoSenderType.buyer
        : TextMessageResponseDtoSenderType.supplier,
    supabaseClient,
    enabled: enableMessageFetch,
  });

  const handleFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, [fileInputRef]);

  // Determine display name for typing indicator
  // If I am a buyer and the room is anonymous, I should appear as '구매자'
  const myDisplayName = useMemo(() => {
    if (role === "buyer") {
      return roomData?.isAnonymous
        ? "구매자"
        : roomData?.userInfo?.name || "구매자";
    }
    return roomData?.supplierInfo?.companyName || "공급자";
  }, [role, roomData]);

  // Typing Indicator Hook (Isolated Channel)
  const { typingUsers, handleTyping, cancelTyping } = useChatTyping({
    roomId,
    userId: currentUserId,
    userName: myDisplayName,
    supabaseClient,
  });

  // Refs for infinite scroll
  const messagesStartRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef<number>(0);
  const prevMessagesLengthRef = useRef<number>(0);

  const [initialScrollDone, setInitialScrollDone] = useState(false);

  // 왼쪽 상대 정보 패널 접힘 상태 (lg 이상에서만 의미가 있다)
  const [isInfoPanelCollapsed, setIsInfoPanelCollapsed] = useState(false);

  // Deduplicate messages
  const uniqueMessages = useMemo(() => {
    const seen = new Set<string>();
    const unique = messages.filter((msg) => {
      if (seen.has(msg.id)) {
        return false;
      }
      seen.add(msg.id);
      return true;
    });

    return unique;
  }, [messages]);

  // Group messages for UI
  const groupedMessages = useMemo(() => {
    return groupMessages(uniqueMessages, currentUserId);
  }, [uniqueMessages, currentUserId]);

  const uniqueMessagesRef = useRef(uniqueMessages);
  useEffect(() => {
    uniqueMessagesRef.current = uniqueMessages;
  }, [uniqueMessages]);

  // Active Contract ID Resolution
  const activeContractId = useMemo(() => {
    const latestContractMessage = [...uniqueMessages]
      .reverse()
      .find(
        (message): message is ContractMessageResponseDto =>
          message.messageType === "contract",
      );

    const candidateContractId =
      roomData?.contractId ??
      (latestContractMessage?.contractInfo?.status === "complete"
        ? latestContractMessage.contractInfo.contractId
        : undefined);

    const hasApprovedCancellation = uniqueMessages.some(
      (message) =>
        message.messageType === "cancellation_request" &&
        message.cancellationRequestData?.status === "approved" &&
        message.cancellationRequestData.contractId === candidateContractId,
    );

    if (hasApprovedCancellation) {
      return undefined;
    }

    // A cancelled contract is no longer active. The room's contractId can be
    // stale until its cancellation side effects have propagated.
    if (latestContractMessage?.contractInfo?.status === "cancelled") {
      return undefined;
    }

    return candidateContractId;
  }, [roomData?.contractId, uniqueMessages]);

  // 입금 대기 중인 현금결제 건이 있는가 — 서버 가드(이미 진행 중인 결제건이
  // 있습니다)와 같은 기준으로, 누르기 전에 [계약하기] 를 막는다(GS 재현 2026-10-12).
  const hasPendingCashDeposit = useMemo(() => {
    const pendingDeposit = uniqueMessages.find(
      (message): message is PaymentPendingMessageResponseDto =>
        message.messageType === "payment_pending" &&
        message.paymentPendingData.status === "PENDING",
    );
    if (!pendingDeposit) return false;

    const { expiresAt } = pendingDeposit.paymentPendingData;
    return expiresAt ? new Date(expiresAt) > new Date() : true;
  }, [uniqueMessages]);

  // Monitor contract completion via Realtime messages
  useEffect(() => {
    const hasContract = uniqueMessages.some(
      (m) =>
        m.messageType === "contract" &&
        (m as ContractMessageResponseDto).contractInfo?.status === "complete",
    );

    if (hasContract) {
      onContractComplete?.();
    }
  }, [uniqueMessages, onContractComplete]);

  // Calculate Latest Quote
  const latestQuote = useMemo(() => {
    let maxDate = 0;
    let latestFromMessages: QuoteMessageResponseDto | null = null;

    uniqueMessages.forEach((msg) => {
      if (msg.messageType === "quote") {
        const msgDate = new Date(msg.createdAt).getTime();
        if (msgDate > maxDate) {
          maxDate = msgDate;
          latestFromMessages = msg as QuoteMessageResponseDto;
        }
      }
    });

    const initialLatest = roomData?.latestQuoteMessage;

    if (!latestFromMessages) return initialLatest ?? null;
    if (!initialLatest) return latestFromMessages;

    const initialDate = new Date(initialLatest.createdAt).getTime();
    return maxDate >= initialDate ? latestFromMessages : initialLatest;
  }, [uniqueMessages, roomData]);

  const latestQuoteId = latestQuote?.id ?? null;

  // Unread messages & scroll hook
  const { unreadCount, isAtBottom, handleScroll, scrollToBottom } =
    useUnreadMessages({
      messages: uniqueMessages,
      currentUserId,
      messagesEndRef,
    });

  // Presence status hook
  const { isActive, statusText } = usePresenceStatus({
    roomId,
    currentUserId,
    otherUserId: partyDisplay.id ?? "",
    supabaseClient,
    initialLastLoginAt:
      role === "buyer"
        ? (roomData?.supplierInfo?.lastLoginAt ?? undefined)
        : (roomData?.userInfo?.lastLoginAt ?? undefined),
  });

  // Auto-mark messages as read
  useAutoMarkRead({
    messages: uniqueMessages,
    isAtBottom,
    isLoading: messagesLoading,
    markMessagesAsRead,
    enabled: !readOnly,
  });

  // Initial Scroll to Bottom (Robust Implementation)
  React.useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // 1. Wait for messages to load
    if (messagesLoading) return;

    // 2. Empty chat is considered initialized
    if (uniqueMessages.length === 0) {
      setInitialScrollDone(true);
      return;
    }

    // 3. Perform initial scroll if not done
    if (!initialScrollDone) {
      requestAnimationFrame(() => {
        container.scrollTop = container.scrollHeight;
        setInitialScrollDone(true);
      });
    }
  }, [messagesLoading, uniqueMessages.length, initialScrollDone]);

  // Resize Observer to handle container height changes (e.g. keyboard open, window resize, embed load)
  // This is critical for "full" variant or embed apps where layout might shift after mount
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      // If we are already at the bottom (or just initialized), keep us at the bottom
      if (isAtBottom) {
        container.scrollTop = container.scrollHeight;
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [isAtBottom]);

  // Infinite Scroll
  useEffect(() => {
    if (
      !messagesStartRef.current ||
      !hasMoreMessages ||
      messagesLoading ||
      !initialScrollDone
    )
      return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !isFetchingPrevious && hasMoreMessages) {
          if (scrollContainerRef.current) {
            prevScrollHeightRef.current =
              scrollContainerRef.current.scrollHeight;
          }
          fetchPreviousMessages();
        }
      },
      { threshold: 0.1, root: scrollContainerRef.current },
    );

    observer.observe(messagesStartRef.current);
    return () => observer.disconnect();
  }, [
    hasMoreMessages,
    isFetchingPrevious,
    fetchPreviousMessages,
    messagesLoading,
    initialScrollDone,
  ]);

  // Scroll Restoration
  React.useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (
      uniqueMessages.length > prevMessagesLengthRef.current &&
      prevScrollHeightRef.current > 0 &&
      prevMessagesLengthRef.current > 0
    ) {
      const newScrollHeight = container.scrollHeight;
      const scrollDiff = newScrollHeight - prevScrollHeightRef.current;

      if (scrollDiff > 0) {
        container.scrollTop = container.scrollTop + scrollDiff;
      }
    }

    prevMessagesLengthRef.current = uniqueMessages.length;
  }, [uniqueMessages.length]);

  // Handlers
  const handleSend = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      const text = state.inputText.trim();
      if (!text || isSending) return;

      // 사용자매뉴얼 R06 5.2.4 12번 «메시지는 최대 1,000자까지 전송 가능합니다.» — 입력란은 1,000자를
      // 넘겨도 받아 두고(글자 수가 빨갛게 바뀐다) 보내려 할 때 막는다. 입력 내용은 지우지 않는다.
      if (text.length > chatControllerSendMessageBodyContentMax) {
        toast.error(
          `메시지는 최대 ${chatControllerSendMessageBodyContentMax.toLocaleString("ko-KR")}자까지 전송 가능합니다.`,
        );
        return;
      }

      sendMessage(text);
      setState((prev) => ({ ...prev, inputText: "" }));

      // Immediately stop typing indicator
      cancelTyping();

      // Keep focus on input after sending
      requestAnimationFrame(() => {
        textInputRef.current?.focus();
      });
    },
    [state.inputText, isSending, sendMessage, cancelTyping],
  );

  // 클립 아이콘·붙여넣기 두 경로가 모두 이 검사를 지난다(기능 159).
  const uploadWithinLimit = useCallback(
    (file: File) => {
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.warning("파일 크기는 최대 10MB까지 업로드 가능합니다.");
        return;
      }
      uploadFileHook(file);
    },
    [uploadFileHook],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      uploadWithinLimit(file);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [uploadWithinLimit, fileInputRef],
  );

  const handleViewQuote = useCallback(
    (messageOrId: string | QuoteMessageResponseDto) => {
      if (typeof messageOrId === "string") {
        const message = uniqueMessagesRef.current.find(
          (m) => m.id === messageOrId,
        );
        if (!message) {
          console.warn(
            "[ChatWindow] Quote message not found in current list:",
            messageOrId,
          );
          return;
        }
        setState((prev) => ({
          ...prev,
          selectedQuote: message as QuoteMessageResponseDto,
        }));
      } else {
        setState((prev) => ({ ...prev, selectedQuote: messageOrId }));
      }
    },
    [],
  );

  const handleQuoteAction = useCallback(
    (messageId: string) => {
      const message = uniqueMessagesRef.current.find((m) => m.id === messageId);
      if (!message) return;

      if (role === "buyer") {
        // 계약 대금은 발주기관이 공급기업에게 직접 지급하므로 [계약하기] 는 결제로 가지 않는다.
        // buyer 앱이 계약 체결 모달(대금 지급 수단 + 계약서류)을 띄운다.
        onCreateContractDirect?.(message);
      } else {
        setState((prev) => ({
          ...prev,
          editingQuote: { message, roomId },
        }));
      }
    },
    [role, roomId, onCreateContractDirect],
  );

  /**
   * 카드로 계약했는데 공급사가 씨마켓 카드결제(이노페이) 가맹이 아닌가 — **방을 열 때마다 묻는다.**
   *
   * 계약 시점의 판정을 메시지로 굳혀 두면, 공급사가 가입을 마친 뒤에도 「미가맹」이라 말하는
   * 안내가 대화에 영영 남는다. 가입이 끝나는 순간은 씨마켓 안에서 일어나 세모에 통보가 오지
   * 않으므로, 알아내는 길은 물어보는 것뿐이다. 가입이 끝나면 다음 조회에서 배너가 사라진다.
   *
   * 카드 계약이 아니면 서버가 씨마켓에 묻지 않고 바로 답하므로 방마다 외부 호출이 늘지 않는다.
   *
   * **조건을 화면에서 조립하지 않는다.** 「카드 계약인가 + 가맹인가」를 여기서 엮었더니
   * 「어느 결제 길인가」가 빠져, 씨마켓과 무관한 세모 안심결제 계약에까지 배너가 떴다.
   * 서버가 판정 하나로 답하고 화면은 그대로 따른다.
   */
  const { data: cardPaymentStatusResp } =
    useContractsControllerGetCardPaymentStatus(activeContractId ?? "", {
      query: { enabled: !!activeContractId },
    });
  const showCardSignupBanner =
    cardPaymentStatusResp?.data?.cmarketCardSignupRequired === true;

  const handleViewContract = useCallback((contractId: string) => {
    setState((prev) => ({ ...prev, selectedContractId: contractId }));
  }, []);

  const handleContractAction = useCallback(
    (messageId: string) => {
      // If it's a cancellation request, we open the modal
      setState((prev) => ({ ...prev, showCancellationModal: true }));
      // Original callback might still be needed for other actions
      onContractAction?.(messageId);
    },
    [onContractAction],
  );

  const handleRequestCancellation = useCallback(
    (reason: string) => {
      if (!activeContractId) return;
      requestContractCancellation({
        contractId: activeContractId,
        reason,
      });
    },
    [activeContractId, requestContractCancellation],
  );

  const handleContractReject = useCallback(
    (messageId: string) => {
      onContractReject?.(messageId);
    },
    [onContractReject],
  );

  const handlePrintReceipt = useCallback((messageId: string) => {
    const message = uniqueMessagesRef.current.find((m) => m.id === messageId);
    if (!message) return;

    const data = (message as any)?.cancellationRequestData;
    const paymentMethod = data?.paymentMethod ?? "CARD";

    // If CARD, open external PG receipt
    if (paymentMethod === "CARD") {
      let tid = (message as any)?.contractInfo?.tid ?? data?.tid;
      if (!tid) {
        tid = (message as any)?.contractInfo?.contractId;
      }

      if (tid) {
        openInnopayReceipt(tid);
      }
    } else {
      // If CASH or others, open SEMO custom receipt modal
      setState((prev) => ({
        ...prev,
        showCancellationReceiptModal: true,
        selectedCancellationMessage: message,
      }));
    }
  }, []);

  const handleLatestQuoteAction = useCallback(() => {
    if (!latestQuote) return;

    if (role === "buyer") {
      onCreateContractDirect?.(latestQuote);
    } else {
      setState((prev) => ({
        ...prev,
        editingQuote: { message: latestQuote, roomId },
      }));
    }
  }, [latestQuote, role, roomId, onCreateContractDirect]);

  const handleLatestViewQuote = useCallback(() => {
    if (latestQuote) {
      handleViewQuote(latestQuote);
    }
  }, [latestQuote, handleViewQuote]);

  const handleQuoteRevisionSubmit = useCallback(
    (data: any) => {
      setState((prev) => ({ ...prev, editingQuote: null }));
      submitQuoteRevision?.(data);
    },
    [submitQuoteRevision],
  );

  const displayAmount = useMemo(() => {
    const amount = state.selectedQuote?.quoteData?.totalAmount;
    return amount ? `${Number(amount).toLocaleString("ko-KR")}원` : "0원";
  }, [state.selectedQuote?.quoteData?.totalAmount]);

  const contextValue = useMemo<ChatActionContextType>(
    () => ({
      onViewQuote: handleViewQuote,
      onQuoteAction: handleQuoteAction,
      onContractAction: handleContractAction,
      onContractReject: handleContractReject,
      onViewContract: handleViewContract,
      onPrintReceipt: handlePrintReceipt,
      onDownloadFile: downloadFile,
      onRequestContractDocuments: async (
        messageId,
        paperCodes,
        idempotencyKey,
      ) => {
        try {
          await requestContractDocuments(roomId, {
            messageId,
            paperCodes,
            idempotencyKey,
          });
          toast.success("계약서류를 요청했습니다.");
          await refetch();
        } catch (error) {
          toast.error(
            error instanceof Error
              ? error.message
              : "계약서류 요청에 실패했습니다. 잠시 후 다시 시도해 주세요.",
          );
          throw error;
        }
      },
      onSubmitContractDocumentInput: async (targetRoomId, paperCode, body) => {
        try {
          await submitContractDocumentInput(targetRoomId, paperCode, {
            idempotencyKey: crypto.randomUUID(),
            ...body,
          });
          toast.success("계약 서류가 제출되었습니다.");
          const cacheKey = [
            ...getChatControllerGetMessagesQueryKey(targetRoomId),
            "infinite",
          ];
          try {
            const freshPage = await chatControllerGetMessages(targetRoomId, {
              limit: 50,
            });
            queryClient.setQueryData(cacheKey, {
              pages: [freshPage],
              pageParams: [undefined],
            });
          } catch {
            queryClient.removeQueries({ queryKey: cacheKey });
            await queryClient.refetchQueries({ queryKey: cacheKey });
          }
        } catch (error) {
          toast.error(
            error instanceof Error
              ? error.message
              : "계약 서류 제출에 실패했습니다. 잠시 후 다시 시도해 주세요.",
          );
          throw error;
        }
      },
      onUploadContractDocumentFile: async (targetRoomId, paperCode, file) => {
        try {
          await uploadContractDocumentFile(targetRoomId, paperCode, file);
          toast.success("계약 서류가 첨부되었습니다.");

          const cacheKey = [
            ...getChatControllerGetMessagesQueryKey(targetRoomId),
            "infinite",
          ];

          // 카드 상태는 서버가 SSOT. 최신 메시지 목록을 직접 fetch 해서 useInfiniteQuery
          // cache 를 통째로 대체 → useChatMessages(structuralSharing:false) + ChatMessage.memo
          // 예외 처리로 카드가 즉시 리렌더된다.
          try {
            const freshPage = await chatControllerGetMessages(targetRoomId, {
              limit: 50,
            });
            queryClient.setQueryData(cacheKey, {
              pages: [freshPage],
              pageParams: [undefined],
            });
          } catch {
            // 직접 fetch 실패 시 fallback: cache 제거 + 강제 refetch
            queryClient.removeQueries({ queryKey: cacheKey });
            await queryClient.refetchQueries({ queryKey: cacheKey });
          }
        } catch (error) {
          // 서버 4xx 사유는 그대로, 영문 내부 문구·서버 오류·연결 끊김은 매뉴얼 R06 5.2.4 14번 문구로.
          toast.error(clientErrorMessage(error, FILE_ATTACH_FAILED_MESSAGE));
          throw error;
        }
      },
      onSubmitContractDocumentBuyerConfirmation:
        role === "buyer"
          ? async (targetRoomId, buyerConfirmation) => {
              try {
                await submitContractDocumentBuyerConfirmation(targetRoomId, {
                  idempotencyKey: crypto.randomUUID(),
                  buyerConfirmation,
                });
                toast.success("최종 확인이 제출되었습니다.");
                const cacheKey = [
                  ...getChatControllerGetMessagesQueryKey(targetRoomId),
                  "infinite",
                ];
                try {
                  const freshPage = await chatControllerGetMessages(
                    targetRoomId,
                    { limit: 50 },
                  );
                  queryClient.setQueryData(cacheKey, {
                    pages: [freshPage],
                    pageParams: [undefined],
                  });
                } catch {
                  queryClient.removeQueries({ queryKey: cacheKey });
                  await queryClient.refetchQueries({ queryKey: cacheKey });
                }
              } catch (error) {
                toast.error(
                  error instanceof Error
                    ? error.message
                    : "최종 확인 제출에 실패했습니다. 잠시 후 다시 시도해 주세요.",
                );
                throw error;
              }
            }
          : undefined,
      onRefresh: refetch,
      // 계약 카드의 [계산서 발급요청]/[카드결제하기] — buyer 앱이 임베드 안에서만 주입한다.
      onOpenCmarketContracts:
        role === "buyer" ? onOpenCmarketContracts : undefined,
      onRetry: resendMessage,
      onImageLoad: () => {
        if (isAtBottom) {
          scrollToBottom("auto");
        }
      },
    }),
    [
      handleViewQuote,
      handleQuoteAction,
      handleContractAction,
      handleContractReject,
      handleViewContract,
      handlePrintReceipt,
      downloadFile,
      roomId,
      refetch,
      queryClient,
      onOpenCmarketContracts,
      role,
      resendMessage,
      isAtBottom,
      scrollToBottom,
    ],
  );

  const isFull = variant === "full";

  if (joinLoading && !roomData) {
    return <ChatWindowSkeleton />;
  }

  if (!roomData) {
    if (isFull) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center bg-white ">
          <p className="text-sm text-red-500 mb-4">
            채팅방 정보를 불러올 수 없습니다.
          </p>
          <Button
            onClick={onClose}
            variant="outline"
            className="rounded-lg text-sm"
          >
            닫기
          </Button>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center p-8 bg-white h-full">
        <p className="text-sm text-red-400">
          채팅방 정보를 불러올 수 없습니다.
        </p>
        <Button onClick={onClose} className="mt-4">
          닫기
        </Button>
      </div>
    );
  }

  const containerClass = isFull
    ? "w-full h-full bg-white flex overflow-hidden"
    : cn("relative w-full h-full bg-white flex overflow-hidden");

  return (
    <ChatActionContext.Provider value={contextValue}>
      <>
        <div className={containerClass}>
          {/* Left: Info Panel — 대화 상대 정보를 대화창보다 먼저 읽도록 왼쪽에 둔다.
              접으면 바깥 폭만 레일 크기로 줄이고 안쪽 320px 칼럼은 그대로 둔다.
              (안쪽까지 같이 줄이면 접었다 펼 때마다 글자가 다시 흐른다) */}
          <div
            className={cn(
              "hidden lg:flex relative shrink-0 border-r border-slate-100 bg-white flex-col overflow-hidden",
              "transition-[width] duration-200 ease-out",
              isInfoPanelCollapsed ? "w-[44px]" : "w-[320px]",
            )}
          >
            <button
              type="button"
              onClick={() => setIsInfoPanelCollapsed((prev) => !prev)}
              aria-expanded={!isInfoPanelCollapsed}
              aria-label={
                isInfoPanelCollapsed ? "상대 정보 펼치기" : "상대 정보 접기"
              }
              title={
                isInfoPanelCollapsed ? "상대 정보 펼치기" : "상대 정보 접기"
              }
              className={cn(
                "absolute top-3 z-10 w-8 h-8 rounded-lg flex items-center justify-center",
                "text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors",
                isInfoPanelCollapsed ? "left-1.5" : "right-2",
              )}
            >
              {isInfoPanelCollapsed ? (
                <PanelLeftOpen className="w-4 h-4" />
              ) : (
                <PanelLeftClose className="w-4 h-4" />
              )}
            </button>

            <div
              aria-hidden={isInfoPanelCollapsed}
              className={cn(
                "w-[320px] h-full shrink-0 flex flex-col",
                // visibility 까지 같이 전환해야 접힌 패널이 탭 이동에서도 빠진다
                "transition-[opacity,visibility] duration-150",
                isInfoPanelCollapsed && "opacity-0 invisible",
              )}
            >
              {slots?.infoPanelContent ? (
                slots.infoPanelContent({
                  supplierInfo: roomData?.supplierInfo,
                  buyerInfo: roomData?.userInfo,
                  role,
                  latestQuoteMessage: latestQuote as any,
                  activeContractId,
                  onQuoteAction: handleLatestQuoteAction,
                  onViewQuote: handleLatestViewQuote,
                })
              ) : (
                <DefaultInfoPanel
                  supplierInfo={roomData?.supplierInfo}
                  buyerInfo={roomData?.userInfo}
                  role={role}
                  isAnonymous={roomData?.isAnonymous ?? false}
                  latestQuoteMessage={latestQuote as any}
                  activeContractId={activeContractId}
                  hasPendingCashDeposit={hasPendingCashDeposit}
                  onQuoteAction={handleLatestQuoteAction}
                  onViewQuote={handleLatestViewQuote}
                  onViewContract={handleViewContract}
                />
              )}
            </div>
          </div>

          {/* Right: Chat Area */}
          <div className="flex-1 flex flex-col min-w-0 bg-white ">
            <ChatHeader
              otherParty={{
                name: partyDisplay.name,
                imageUrl: partyDisplay.imageUrl,
                trustScore: undefined,
                trustPercentile: partyDisplay.trustPercentile,
                lastLoginAt: undefined,
                id: partyDisplay.id,
              }}
              role={role}
              onClose={onClose}
              isActive={isActive}
              statusText={statusText}
              hideClose={hideClose}
            />

            {/* 메시지 목록 밖에 둔다 — 대화가 길어져도 위로 밀려 올라가지 않아야 한다. */}
            {showCardSignupBanner && <CardPaymentSignupBanner role={role} />}

            <div className="flex-1 bg-slate-50/50  relative min-h-0">
              <div
                ref={scrollContainerRef}
                className="absolute inset-0 px-3 pb-3 pt-0 sm:px-6 sm:pb-6 sm:pt-0 overflow-y-auto custom-scrollbar"
                onScroll={handleScroll}
              >
                {messagesLoading ||
                isInitializing ||
                (joinApiLoading && !disableRoomFetch) ? (
                  <ChatListSkeleton count={4} />
                ) : (
                  <div className="space-y-4 sm:space-y-6 w-full">
                    <div ref={messagesStartRef} className="h-px w-full" />

                    {isFetchingPrevious && (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                      </div>
                    )}

                    {uniqueMessages.length === 0 ? (
                      <div className="text-center py-8 text-slate-600 ">
                        메시지가 없습니다. 첫 메시지를 보내보세요!
                      </div>
                    ) : (
                      <>
                        {groupedMessages.map((group) => (
                          <React.Fragment key={group.date}>
                            <div className="py-2 -mx-3 sm:-mx-6 px-3 sm:px-6 pointer-events-none">
                              <ChatDateDivider
                                date={formatDateKoreanLong(group.date)}
                              />
                            </div>
                            <div className="space-y-1">
                              {group.messages.map((msg) => (
                                <ChatMessage
                                  key={msg.id}
                                  message={msg}
                                  messageId={msg.id}
                                  partyName={partyDisplay.name}
                                  amount={displayAmount}
                                  role={role}
                                  currentUserId={currentUserId}
                                  isLatestQuote={msg.id === latestQuoteId}
                                  contractId={activeContractId}
                                  hasPendingCashDeposit={hasPendingCashDeposit}
                                  isFirstInGroup={msg.isFirstInGroup}
                                  isLastInGroup={msg.isLastInGroup}
                                  showAvatar={msg.showAvatar}
                                  showName={msg.showName}
                                  showTime={msg.showTime}
                                />
                              ))}
                            </div>
                          </React.Fragment>
                        ))}
                      </>
                    )}
                    <div className="px-4 pb-2 text-xs text-slate-500 animate-pulse min-h-[24px]">
                      {typingUsers[0] && (
                        <TypingIndicator
                          partyName={role === "supplier" ? "구매자" : "공급사"}
                        />
                      )}
                    </div>
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>
              {!messagesLoading && (unreadCount > 0 || !isAtBottom) && (
                <Button
                  onClick={() => scrollToBottom()}
                  variant={unreadCount > 0 ? "default" : "outline"}
                  size={unreadCount > 0 ? "default" : "icon"}
                  className={cn(
                    "absolute bottom-4 left-1/2 -translate-x-1/2 z-10",
                    "shadow-lg transition-all duration-200",
                    "animate-in slide-in-from-bottom-4 fade-in",
                    unreadCount > 0
                      ? "rounded-full gap-2 h-10 px-4"
                      : "w-10 h-10 rounded-full",
                  )}
                  aria-label="맨 아래로 스크롤"
                >
                  {unreadCount > 0 && (
                    <span className="text-sm font-medium">
                      새 메시지 {unreadCount}개
                    </span>
                  )}
                  <ArrowDown
                    className={cn("w-4 h-4", unreadCount === 0 && "w-5 h-5")}
                  />
                </Button>
              )}{" "}
            </div>

            {readOnly ? (
              <div className="p-4 bg-slate-50  border-t border-slate-200  text-center text-slate-500  text-sm">
                관리자 모드에서는 메시지를 보낼 수 없습니다.
              </div>
            ) : (
              <ChatInput
                ref={textInputRef}
                value={state.inputText}
                onChange={(value) => {
                  setState((prev) => ({ ...prev, inputText: value }));
                  handleTyping();
                }}
                onSend={handleSend}
                onFileClick={handleFileClick}
                onFileChange={handleFileChange}
                fileInputRef={fileInputRef}
                onUpload={uploadWithinLimit}
                disabled={
                  isUploading || isSubmittingQuote || isCreatingContract
                }
                maxLength={chatControllerSendMessageBodyContentMax}
              />
            )}
          </div>
        </div>

        {state.selectedQuote &&
          !state.editingQuote &&
          slots?.quoteDetailModal && (
            <Dialog
              open={!!state.selectedQuote}
              onOpenChange={(open) =>
                !open && setState((prev) => ({ ...prev, selectedQuote: null }))
              }
            >
              <DialogContent
                size="2xl"
                className="p-0 overflow-hidden h-[90vh] sm:max-w-5xl z-[220] border-0 shadow-none"
                overlayClassName="z-[215]"
                hideClose
              >
                <DialogTitle className="sr-only">견적서 상세</DialogTitle>
                <DialogDescription className="sr-only">
                  견적서의 상세 내용을 확인합니다.
                </DialogDescription>
                {slots.quoteDetailModal({
                  message:
                    uniqueMessages.find(
                      (m) => m.id === state.selectedQuote!.id,
                    ) || state.selectedQuote,
                  onClose: () =>
                    setState((prev) => ({ ...prev, selectedQuote: null })),
                  fileNameContext: {
                    projectTitle: roomData.quoteRequest?.projectName,
                  },
                })}
              </DialogContent>
            </Dialog>
          )}

        {state.editingQuote && slots?.quoteActionModal && (
          <Dialog
            open={!!state.editingQuote}
            onOpenChange={(open) =>
              !open && setState((prev) => ({ ...prev, editingQuote: null }))
            }
          >
            <DialogContent
              size="full"
              className="flex flex-col p-0 overflow-hidden z-[220] border-0"
              overlayClassName="z-[215]"
              hideClose
            >
              <DialogTitle className="sr-only">견적서 수정</DialogTitle>
              <DialogDescription className="sr-only">
                견적서 내용을 수정합니다.
              </DialogDescription>
              {slots.quoteActionModal({
                message: state.editingQuote.message,
                roomId: state.editingQuote.roomId,
                otherPartyName: partyDisplay.name,
                onClose: () =>
                  setState((prev) => ({ ...prev, editingQuote: null })),
                onComplete: () => {
                  setState((prev) => ({ ...prev, editingQuote: null }));
                  onContractComplete?.();
                },
                onSubmitRevision: handleQuoteRevisionSubmit,
              })}
            </DialogContent>
          </Dialog>
        )}

        {state.selectedContractId && slots?.contractDetailModal && (
          <Dialog
            open={!!state.selectedContractId}
            onOpenChange={(open) =>
              !open &&
              setState((prev) => ({ ...prev, selectedContractId: null }))
            }
          >
            <DialogContent
              size="2xl"
              className="p-0 overflow-hidden h-[90vh] sm:max-w-5xl z-[220] border-0 shadow-none"
              overlayClassName="z-[215]"
              hideClose
            >
              <DialogTitle className="sr-only">계약서 상세</DialogTitle>
              <DialogDescription className="sr-only">
                계약서의 상세 내용을 확인합니다.
              </DialogDescription>
              {slots.contractDetailModal({
                contractId: state.selectedContractId,
                onClose: () =>
                  setState((prev) => ({ ...prev, selectedContractId: null })),
              })}
            </DialogContent>
          </Dialog>
        )}

        {state.showCancellationModal &&
          slots?.cancellationRequestModal?.({
            isOpen: state.showCancellationModal,
            onClose: () =>
              setState((prev) => ({ ...prev, showCancellationModal: false })),
            onSubmit: (reason) => {
              handleRequestCancellation(reason);
              setState((prev) => ({ ...prev, showCancellationModal: false }));
            },
          })}

        {state.showCancellationReceiptModal &&
          state.selectedCancellationMessage && (
            <CancellationReceiptModal
              message={state.selectedCancellationMessage}
              onClose={() =>
                setState((prev) => ({
                  ...prev,
                  showCancellationReceiptModal: false,
                  selectedCancellationMessage: null,
                }))
              }
              context={{
                projectName: roomData?.quoteRequest?.projectName,
                buyerName: roomData?.userInfo?.name || undefined,
                supplierName: roomData?.supplierInfo?.companyName || undefined,
                totalAmount:
                  state.selectedCancellationMessage?.cancellationRequestData
                    ?.totalAmount ||
                  state.selectedCancellationMessage?.contractInfo
                    ?.totalAmount ||
                  0,
              }}
            />
          )}
      </>
    </ChatActionContext.Provider>
  );
}

function DetailRow({
  icon: Icon,
  label,
  value,
}: {
  icon: any;
  label: string;
  value?: string | null;
}) {
  const displayValue = value || "-";

  // 이름과 값을 한 줄에 나란히 둔다. 아이콘 배경 상자를 빼서 줄 높이를 아이콘
  // 크기까지 낮췄다 — 항목 수가 늘어도 패널이 세로로 길어지지 않는다.
  // 값은 잘라내지 않고 줄바꿈한다. 잘라내면 주소 뒷부분(동/호수)과 이메일
  // 도메인처럼 정작 필요한 쪽이 사라진다.
  return (
    <div className="flex items-start gap-2 leading-[1.45]">
      <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-[3px]" />
      {/* 이름은 값보다 한 단계 작고 연하게 — 같은 크기면 이름이 값만큼 튄다 */}
      <span className="w-[58px] shrink-0 text-xs text-slate-400 mt-[1px]">
        {label}
      </span>
      <span
        className={cn(
          "flex-1 min-w-0 text-sm font-medium break-words",
          !value ? "text-slate-400" : "text-slate-800",
        )}
      >
        {displayValue}
      </span>
    </div>
  );
}

/**
 * Default Info Panel
 */
function DefaultInfoPanel({
  supplierInfo,
  buyerInfo,
  role,
  isAnonymous,
  latestQuoteMessage,
  activeContractId,
  hasPendingCashDeposit,
  onQuoteAction,
  onViewQuote,
  onViewContract,
}: {
  supplierInfo: SupplierDto | undefined;
  buyerInfo: BuyerDto | undefined;
  role: "buyer" | "supplier";
  isAnonymous: boolean;
  latestQuoteMessage?: QuoteMessageResponseDto;
  activeContractId?: string;
  hasPendingCashDeposit?: boolean;
  onQuoteAction?: () => void;
  onViewQuote?: () => void;
  onViewContract?: (contractId: string) => void;
}) {
  const isBuyer = role === "buyer";

  // Extract display info
  const displayInfo = isBuyer
    ? {
        name: supplierInfo?.companyName || "업체명",
        imageUrl: supplierInfo?.profileImageUrl || null,
        trustPercentile: supplierInfo?.trustPercentile,
      }
    : {
        name: buyerInfo?.name || "고객",
        imageUrl: buyerInfo?.profileImageUrl || null,
        trustPercentile: buyerInfo?.trustPercentile,
      };

  const infoTitle = isBuyer ? "공급자 정보" : "구매자 정보";
  const guideTitle = isBuyer ? "구매자 가이드" : "공급자 가이드";
  const tipText = isBuyer
    ? "명확한 요구사항 전달은 만족스러운 결과를 보장합니다."
    : "적극적인 소통은 계약 성공률을 높입니다.";

  const checklistItems = isBuyer
    ? [
        "견적서 상세 내용 확인",
        "납기일 및 배송 조건 협의",
        "계약 전 최종 금액 재확인",
      ]
    : [
        "고객 요청사항 파악",
        "수정된 견적 금액 재확인",
        "납기 일정 준수 가능 여부",
      ];

  return (
    <div className="flex flex-col h-full">
      {/* 1. Header & Profile */}
      <div className="p-4 pb-3 bg-white text-slate-900">
        <h3 className="text-2xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
          {infoTitle}
        </h3>
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 p-0.5 shrink-0">
            <PartyAvatar
              name={displayInfo.name}
              imageUrl={displayInfo.imageUrl}
              className="w-full h-full rounded-[10px]"
            />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 text-base truncate leading-tight mb-0.5">
              {displayInfo.name}
            </div>
            {displayInfo.trustPercentile ? (
              <Badge className="bg-primary/5 text-primary-text hover:bg-primary/10 border-primary/15 px-1.5 py-0 h-5 text-2xs">
                <ShieldCheck className="w-2.5 h-2.5 mr-1" />
                상위 {displayInfo.trustPercentile}%
              </Badge>
            ) : (
              <Badge className="bg-slate-50 text-slate-500 hover:bg-slate-100 border-slate-100 px-1.5 py-0 h-5 text-2xs">
                <ShieldCheck className="w-2.5 h-2.5 mr-1" />
                신규 회원
              </Badge>
            )}
          </div>
        </div>
      </div>

      <Separator />

      {/* 2. Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto min-h-0 bg-white">
        {/* Detailed Info Section (Always Visible) */}
        <div className="p-4">
          {isBuyer ? (
            // Buyer viewing Supplier Info (Anonymity Check)
            <MaskedSection
              isMasked={isAnonymous}
              scale="compact"
              barCount={supplierInfo?.email ? 6 : 5}
              message={
                <>
                  공급사 정보는
                  <br />
                  계약 후에 확인할 수 있어요
                </>
              }
            >
              <div className="space-y-2">
                <DetailRow
                  icon={User}
                  label="대표자"
                  value={supplierInfo?.ceoName}
                />
                <DetailRow
                  icon={FileText}
                  label="사업자 번호"
                  value={supplierInfo?.businessNumber}
                />
                <DetailRow
                  icon={MapPin}
                  label="주소"
                  value={supplierInfo?.addressFull}
                />
                {/* 이름과 번호를 한 줄에 묶으면 길이가 넘쳐 둘 다 잘렸다 — 따로 읽힌다 */}
                <DetailRow
                  icon={UserRound}
                  label="담당자"
                  value={supplierInfo?.managerName}
                />
                <DetailRow
                  icon={Phone}
                  label="연락처"
                  value={
                    supplierInfo?.managerPhone || supplierInfo?.companyPhone
                  }
                />
                {supplierInfo?.email && (
                  <DetailRow
                    icon={Mail}
                    label="이메일"
                    value={supplierInfo.email}
                  />
                )}
              </div>
            </MaskedSection>
          ) : (
            // Supplier viewing Buyer Info (Anonymity Check)
            <MaskedSection
              isMasked={isAnonymous}
              scale="compact"
              barCount={3}
              message={
                <>
                  구매자 정보는
                  <br />
                  계약 후에 확인할 수 있어요
                </>
              }
            >
              <div className="space-y-2">
                <DetailRow
                  icon={UserRound}
                  label="담당자"
                  value={buyerInfo?.name}
                />
                <DetailRow
                  icon={Phone}
                  label="연락처"
                  value={buyerInfo?.phone}
                />
                <DetailRow
                  icon={Mail}
                  label="이메일"
                  value={buyerInfo?.email}
                />
              </div>
            </MaskedSection>
          )}
        </div>

        <Separator className="my-0.5" />

        {/* Latest Quote or Guide Section */}
        <div className="p-4 bg-white min-h-[180px]">
          {latestQuoteMessage ? (
            <div className="flex flex-col animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="flex items-center justify-between mb-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-3.5 bg-primary rounded-full" />
                  <h3 className="font-semibold text-slate-900 text-sm">
                    최신 견적
                  </h3>
                </div>
                {latestQuoteMessage.quoteData?.versionNumber && (
                  <span className="text-2xs font-medium text-slate-400 bg-white px-1.5 py-0 rounded border border-slate-200">
                    v{latestQuoteMessage.quoteData.versionNumber}
                  </span>
                )}
              </div>

              {(() => {
                const sidebarContractId =
                  latestQuoteMessage?.quoteData?.contractId || activeContractId;
                return (
                  <QuoteCard
                    message={latestQuoteMessage}
                    role={role}
                    sender={
                      latestQuoteMessage.senderType ===
                      (isBuyer ? "buyer" : "supplier")
                        ? "me"
                        : "other"
                    }
                    onViewQuote={onViewQuote || (() => {})}
                    onContract={
                      sidebarContractId && onViewContract
                        ? () => onViewContract(sidebarContractId)
                        : isBuyer
                          ? onQuoteAction
                          : undefined
                    }
                    onEditQuote={onQuoteAction}
                    isLatestQuote={true}
                    contractId={sidebarContractId}
                    hasPendingCashDeposit={hasPendingCashDeposit}
                  />
                );
              })()}
            </div>
          ) : (
            <>
              <h3 className="font-semibold text-slate-900 mb-2.5 text-sm">
                {guideTitle}
              </h3>
              <div className="space-y-2.5">
                <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-6 h-6 bg-primary/5 text-primary-text rounded-lg flex items-center justify-center">
                      <ShieldCheck className="w-3.5 h-3.5" />
                    </div>
                    <div className="text-sm font-semibold text-slate-900">
                      체크리스트
                    </div>
                  </div>
                  <div className="space-y-2 text-sm text-slate-600">
                    {checklistItems.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <div className="w-1 h-1 rounded-full bg-primary/40 mt-[7px] shrink-0" />
                        <span className="leading-snug">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="flex items-start gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5 text-[#ea8c55] shrink-0 mt-[2px]" />
                    <div className="text-sm text-amber-800 leading-snug">
                      <span className="font-semibold">Tip:</span> {tipText}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export const ChatWindow = React.memo(ChatWindowComponent);
ChatWindow.displayName = "ChatWindow";
